import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, LogOut, Search, ChevronLeft, ChevronRight, Loader2, Inbox, ArrowUpRight, Layers, Settings, ClipboardList, Mail } from "lucide-react";
import { toast } from "sonner";
import { adminCheck, adminCBList, adminLogout } from "../lib/api";

const PAGE = 25;
const statusColor = {
  active: "bg-emerald-500/15 text-emerald-400",
  paused: "bg-amber-500/15 text-amber-400",
  cancelled: "bg-red-500/15 text-red-400",
  past_due: "bg-red-500/15 text-red-400",
};

export const AdminNav = ({ active }) => {
  const navigate = useNavigate();
  const tabs = [
    { key: "projects", label: "Preview Projects", icon: ClipboardList, path: "/admin" },
    { key: "requests", label: "Requests", icon: Inbox, path: "/admin/requests" },
    { key: "cb", label: "Continue Building", icon: Layers, path: "/admin/continue-building" },
    { key: "startups", label: "Startups", icon: Rocket, path: "/admin/startups" },
    { key: "email", label: "Email", icon: Mail, path: "/admin/email" },
    { key: "settings", label: "Settings", icon: Settings, path: "/admin/settings" },
  ];
  return (
    <div className="flex gap-1 mt-4 border-b border-white/10 -mb-px">
      {tabs.map((t) => {
        const Icon = t.icon;
        const on = active === t.key;
        return (
          <button
            key={t.key}
            onClick={() => navigate(t.path)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${on ? "border-[#D4AF37] text-white" : "border-transparent text-[#71717A] hover:text-white"}`}
            data-testid={`admin-nav-${t.key}`}
          >
            <Icon size={15} /> {t.label}
          </button>
        );
      })}
    </div>
  );
};

export default function AdminContinueBuilding() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminCheck()
      .then((r) => {
        if (!r.admin) { setAuthorized(false); navigate("/"); return; }
        setAuthorized(true);
      })
      .catch(() => { setAuthorized(false); navigate("/"); });
  }, [navigate]);

  const load = useCallback(async (pageNum, q) => {
    setLoading(true);
    try {
      const data = await adminCBList({ limit: PAGE, offset: (pageNum - 1) * PAGE, search: q || undefined });
      setItems(data.items);
      setTotal(data.total);
      setPage(pageNum);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authorized) load(1, ""); }, [authorized, load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  if (authorized === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]"><Loader2 className="animate-spin text-[#D4AF37]" size={30} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="admin-cb-list">
      <header className="border-b border-white/10 bg-[#0B0B0B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Rocket className="text-[#D4AF37]" size={18} /></div>
              <div><p className="font-display font-bold leading-tight">Command Center</p><p className="text-xs text-[#71717A]">Continue Building Manager</p></div>
            </div>
            <button onClick={async () => { await adminLogout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="admin-logout-btn">
              <LogOut size={16} /> Sign out
            </button>
          </div>
          <AdminNav active="cb" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <form onSubmit={(e) => { e.preventDefault(); load(1, search); }} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 mb-6 max-w-md">
          <Search size={17} className="text-[#71717A]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by project or customer email..." className="w-full bg-transparent outline-none text-sm placeholder-[#71717A]" data-testid="admin-cb-search" />
        </form>

        <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#D4AF37]" size={26} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-20" data-testid="admin-cb-empty">
              <Inbox className="mx-auto text-[#3f3f46] mb-3" size={34} />
              <p className="font-display font-semibold">No active build programs</p>
              <p className="text-sm text-[#71717A] mt-1">Customers appear here after they subscribe to the Continue Building Program.</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-cb-table">
              <thead>
                <tr className="text-left text-[#71717A] border-b border-white/10">
                  <th className="font-medium px-5 py-3">Project</th>
                  <th className="font-medium px-5 py-3 hidden md:table-cell">Customer</th>
                  <th className="font-medium px-5 py-3">Subscription</th>
                  <th className="font-medium px-5 py-3 hidden sm:table-cell">Progress</th>
                  <th className="font-medium px-5 py-3 hidden lg:table-cell">Renewal</th>
                  <th className="font-medium px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} onClick={() => navigate(`/admin/continue-building/${p.id}`)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors" data-testid={`admin-cb-row-${p.id}`}>
                    <td className="px-5 py-4 font-medium">{p.project_name}</td>
                    <td className="px-5 py-4 text-[#A1A1AA] hidden md:table-cell">{p.customer_email}</td>
                    <td className="px-5 py-4"><span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs capitalize ${statusColor[p.subscription_status] || "bg-white/10"}`}>{p.subscription_status}</span></td>
                    <td className="px-5 py-4 hidden sm:table-cell">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#D4AF37]" style={{ width: `${p.progress_percentage || 0}%` }} /></div>
                        <span className="text-xs text-[#71717A]">{p.progress_percentage || 0}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-[#A1A1AA] hidden lg:table-cell">{p.renewal_date || "—"}</td>
                    <td className="px-5 py-4 text-right"><ArrowUpRight size={16} className="inline text-[#71717A]" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && total > PAGE && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button disabled={page <= 1} onClick={() => load(page - 1, search)} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-4 py-2 text-sm disabled:opacity-40 hover:bg-white/5"><ChevronLeft size={16} /> Prev</button>
            <span className="text-sm text-[#71717A] px-2">Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => load(page + 1, search)} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-4 py-2 text-sm disabled:opacity-40 hover:bg-white/5">Next <ChevronRight size={16} /></button>
          </div>
        )}
      </main>
    </div>
  );
}
